// Power dialer campaign store.
// Manages a queue of numbers to call, paces dialing based on user-chosen mode,
// and tracks per-number status. All state persists to localStorage.

import { create } from 'zustand'
import useSipStore from './sipStore'

const STORAGE_KEY = 'kaliai_campaigns'

/**
 * Campaign shape:
 *   {
 *     id, name, createdAt,
 *     mode: 'preview' | 'power' | 'predictive',
 *     concurrency: number (predictive only),
 *     intercallDelay: number (seconds between calls, all modes),
 *     status: 'idle' | 'running' | 'paused' | 'done',
 *     numbers: [{ raw, status, notes?, callId?, startedAt?, endedAt?, durationSec? }]
 *   }
 */

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}
function save(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)) } catch {}
}

const useCampaignStore = create((set, get) => ({
  campaigns: load(),
  activeCampaignId: null,
  pollTimer: null,

  // ---- CRUD ----
  create: (name, mode, numbers) => {
    const id = 'cmp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6)
    const campaign = {
      id,
      name: name || `קמפיין ${new Date().toLocaleString('he-IL')}`,
      createdAt: new Date().toISOString(),
      mode: mode || 'power',
      concurrency: 2,
      intercallDelay: 2,
      status: 'idle',
      numbers: numbers.map(n => ({
        raw: String(n).trim(),
        status: 'pending',  // pending | dialing | answered | failed | done
        notes: '',
      })).filter(x => x.raw),
    }
    const next = [campaign, ...get().campaigns]
    save(next)
    set({ campaigns: next })
    return id
  },

  delete: (id) => {
    const next = get().campaigns.filter(c => c.id !== id)
    save(next)
    set({ campaigns: next })
    if (get().activeCampaignId === id) {
      get().stop()
    }
  },

  update: (id, patch) => {
    const next = get().campaigns.map(c => c.id === id ? { ...c, ...patch } : c)
    save(next)
    set({ campaigns: next })
  },

  updateNumber: (campaignId, index, patch) => {
    const next = get().campaigns.map(c => {
      if (c.id !== campaignId) return c
      const numbers = c.numbers.slice()
      numbers[index] = { ...numbers[index], ...patch }
      return { ...c, numbers }
    })
    save(next)
    set({ campaigns: next })
  },

  // ---- Runtime ----
  start: (id) => {
    const c = get().campaigns.find(x => x.id === id)
    if (!c) return
    get().update(id, { status: 'running' })
    set({ activeCampaignId: id })
    get()._tick(id)
  },

  pause: (id) => {
    get().update(id, { status: 'paused' })
    const t = get().pollTimer
    if (t) clearTimeout(t)
    set({ pollTimer: null })
  },

  resume: (id) => {
    get().update(id, { status: 'running' })
    set({ activeCampaignId: id })
    get()._tick(id)
  },

  stop: () => {
    const id = get().activeCampaignId
    if (id) {
      const c = get().campaigns.find(x => x.id === id)
      if (c && c.status === 'running') {
        get().update(id, { status: 'paused' })
      }
    }
    const t = get().pollTimer
    if (t) clearTimeout(t)
    set({ pollTimer: null, activeCampaignId: null })
  },

  // ---- Internal: the dialing tick ----
  _tick: (id) => {
    const t = get().pollTimer
    if (t) clearTimeout(t)

    const campaign = get().campaigns.find(c => c.id === id)
    if (!campaign || campaign.status !== 'running') return

    // Find pending + currently-dialing
    const pending = campaign.numbers.filter(n => n.status === 'pending').length
    const dialing = campaign.numbers.filter(n => n.status === 'dialing').length
    const done = campaign.numbers.length - pending - dialing

    // Done?
    if (pending === 0 && dialing === 0) {
      get().update(id, { status: 'done' })
      set({ activeCampaignId: null, pollTimer: null })
      return
    }

    // Decide how many to dial in this tick
    const sip = useSipStore.getState()
    const limit = campaign.mode === 'predictive' ? campaign.concurrency
                : campaign.mode === 'power'      ? 1
                : 0  // preview = manual

    const slotsAvailable = limit - dialing
    if (limit > 0 && slotsAvailable > 0 && pending > 0) {
      // Check if there's a live SIP call
      const sipInCall = sip.inCall || sip.isRinging
      const canDialNow = !sipInCall || campaign.mode === 'predictive'

      if (canDialNow) {
        // Find first pending and dial it
        const idx = campaign.numbers.findIndex(n => n.status === 'pending')
        if (idx >= 0) {
          get()._dialOne(id, idx)
        }
      }
    }

    // Reschedule
    const next = setTimeout(() => get()._tick(id), 1500)
    set({ pollTimer: next })
  },

  _dialOne: (id, index) => {
    const campaign = get().campaigns.find(c => c.id === id)
    if (!campaign) return
    const target = campaign.numbers[index]
    if (!target || target.status !== 'pending') return

    get().updateNumber(id, index, {
      status: 'dialing',
      startedAt: new Date().toISOString(),
    })

    const sip = useSipStore.getState()
    try {
      // Always record campaign calls
      sip.dial(target.raw, { record: true })
      // We don't get a direct promise from dial - rely on SIP events to update status.
      // Set up one-shot listener via polling SIP state (simpler than wiring deep events)
      get()._watchCall(id, index)
    } catch (e) {
      get().updateNumber(id, index, {
        status: 'failed',
        cause: e.message,
        endedAt: new Date().toISOString(),
      })
    }
  },

  _watchCall: (campaignId, index) => {
    let lastInCall = false
    const start = Date.now()
    const poll = setInterval(() => {
      const sip = useSipStore.getState()
      const campaign = get().campaigns.find(c => c.id === campaignId)
      if (!campaign) { clearInterval(poll); return }

      // Timeout: stuck dialing > 90s
      if (Date.now() - start > 90_000) {
        clearInterval(poll)
        get().updateNumber(campaignId, index, {
          status: 'failed',
          cause: 'timeout',
          endedAt: new Date().toISOString(),
        })
        return
      }

      if (sip.inCall) {
        lastInCall = true
        get().updateNumber(campaignId, index, { status: 'answered' })
      } else if (lastInCall && !sip.inCall) {
        // Call ended
        clearInterval(poll)
        const duration = sip.callStartTime ? Math.floor((Date.now() - sip.callStartTime) / 1000) : 0
        get().updateNumber(campaignId, index, {
          status: 'done',
          endedAt: new Date().toISOString(),
          durationSec: duration,
        })
      } else if (sip.error && !lastInCall) {
        // Failed before answer
        clearInterval(poll)
        get().updateNumber(campaignId, index, {
          status: 'failed',
          cause: sip.error,
          endedAt: new Date().toISOString(),
        })
      }
    }, 500)
  },

  // Preview mode: user clicks "call next"
  dialNext: (id) => {
    const c = get().campaigns.find(x => x.id === id)
    if (!c) return
    const idx = c.numbers.findIndex(n => n.status === 'pending')
    if (idx < 0) return
    get()._dialOne(id, idx)
  },

  // Reset all failed/done back to pending so the campaign can re-run
  resetCampaign: (id) => {
    const c = get().campaigns.find(x => x.id === id)
    if (!c) return
    const numbers = c.numbers.map(n => ({
      raw: n.raw,
      status: 'pending',
      notes: n.notes || '',
    }))
    get().update(id, { status: 'idle', numbers })
  },
}))

export default useCampaignStore
